import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, lighten, Grid, TextField, Button, Typography, IconButton } from "@mui/material";
import { useIntl } from "react-intl";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import { TemplateListItemProps } from "../types";
import { ReportRule, Rule, RuleType } from "./types";
import useEditedStyles, { useEditedColor } from "../../../../hooks/useEditedStyles";
import TimeTextField from "../TimeTextField";
import { useRuleTypes } from "./ThermowatchTemplate";
import ZoneEvent from "../zoneevent/ZoneEvent";
import { valueEdited } from "../utils";
import { onNumberInputClick } from "../../../../utils/utils";

interface ReportItemProps extends TemplateListItemProps<Rule<ReportRule>> {}
interface RulesObject extends Omit<Rule<ReportRule>, "uuid" | "name" | "deleted"> {}

const defaultTemeratureRule: ReportRule = {
  e: "215A",
  delay: 600,
  hyst: 0.5,
  delta: 1,
};
const defaultHumidityRule: ReportRule = {
  e: "218A",
  delay: 600,
  hyst: 5,
  delta: 10,
};

const ReportItem = ({ index, value, saved, onChange, onDelete, onRestore, disabled }: ReportItemProps) => {
  const [name, setName] = useState(value.name || "");
  const [rules, setRules] = useState<RulesObject | null>(null);

  const { formatMessage } = useIntl();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const theme = useTheme();
  const ruleTypes = useRuleTypes();

  const addRule = useCallback(
    (type: keyof RulesObject) => {
      if (!rules || rules[type] !== undefined) {
        return;
      }
      value[type] = type === "temperature" ? defaultTemeratureRule : defaultHumidityRule;
      setRules((old) => ({ ...old, [type]: value[type] }));
      onChange(index, value);
    },
    [rules, value, index, onChange]
  );

  const removeRule = useCallback(
    (type: keyof RulesObject) => {
      if (!rules || rules[type] === undefined) {
        return;
      }
      delete value[type];
      setRules((old) => {
        if (!old) {
          return old;
        }
        delete old[type];
        return { ...old };
      });
      onChange(index, value);
    },
    [rules, value, index, onChange]
  );

  const onRuleChange = useCallback(
    (key: keyof RulesObject, newValue: ReportRule) => {
      setRules((old) => {
        if (!old) {
          return old;
        }
        old[key] = newValue;
        return { ...old };
      });
      value[key] = newValue;
      onChange(index, value);
    },
    [value, index, onChange]
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { uuid, name, deleted, ...other } = value;
    setName(name || "");
    setRules(other);
  }, [value]);

  const addRules = useMemo(
    () =>
      Object.entries(ruleTypes)
        .filter(([key]) => !rules || rules[key as keyof RulesObject] === undefined)
        .map(([key, value]: [string, RuleType]) => (
          <Grid item key={`rule-${key}`}>
            <Button variant="outlined" startIcon={value.icon} color="secondary" onClick={() => addRule(key as keyof RulesObject)}>
              {value.label}
            </Button>
          </Grid>
        )),
    [rules, ruleTypes, addRule]
  );

  const ruleItems = useMemo(
    () =>
      Object.entries(ruleTypes)
        .filter(([key]) => rules?.[key as keyof RulesObject] !== undefined)
        .map(([key, v]: [string, RuleType]) => (
          <RuleItem
            key={`rule-${key}`}
            ruleKey={key as keyof RulesObject}
            type={v}
            value={rules?.[key as keyof RulesObject] as ReportRule}
            saved={saved ? (saved[key as keyof RulesObject] as ReportRule) : null}
            onChange={onRuleChange}
            onRemove={removeRule}
            disabled={disabled || value.deleted}
          />
        )),
    [rules, ruleTypes, disabled, value, saved, onRuleChange, removeRule]
  );

  return (
    <TitledCard
      title={
        (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
        formatMessage({ defaultMessage: "Report{name, select, null { #{index}} other {: {name}}}" }, { name: name || null, index: name ? null : index + 1 })
      }
      color={value.deleted ? editedBackground : disabled ? lighten(theme.palette.primary.main, 0.5) : theme.palette.primary.main}
      contrastColor={value.deleted ? editedText : theme.palette.secondary.contrastText}
    >
      <Grid container spacing={1} padding={1}>
        <Grid item xs={12} container flexDirection="row-reverse" alignContent="flex-start" justifyContent="space-between">
          <Grid item>
            {!value.deleted && !disabled && (
              <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={() => onDelete(index)}>
                {formatMessage({ defaultMessage: "Remove" })}
              </Button>
            )}
            {value.deleted && !disabled && (
              <Button color="inherit" onClick={() => onRestore(index)}>
                {formatMessage({ defaultMessage: "Restore" })}
              </Button>
            )}
          </Grid>
          <Grid item alignSelf="flex-start" flex={1}>
            <TextField
              label={formatMessage({ defaultMessage: "Name" })}
              variant="filled"
              value={name}
              className={valueEdited(saved?.name, name, value.name) ? editedStyle.input : ""}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => {
                if (name) {
                  value.name = name;
                } else {
                  delete value.name;
                }
                onChange(index, value);
              }}
              size="small"
              sx={{ minWidth: 220, width: 220, margin: 0 }}
              disabled={disabled || value.deleted}
            />
          </Grid>
        </Grid>
        {ruleItems}
        {addRules.length > 0 && !disabled && !value.deleted && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Add rule:" })}</Typography>
            </Grid>
            <Grid item xs={12} container spacing={1} flexDirection="row">
              {addRules}
            </Grid>
          </>
        )}
      </Grid>
    </TitledCard>
  );
};

interface RuleItemProps {
  ruleKey: keyof RulesObject;
  type: RuleType;
  value: ReportRule;
  saved: ReportRule | null;
  disabled?: boolean;
  onChange: (key: keyof RulesObject, value: ReportRule) => void;
  onRemove: (key: keyof RulesObject) => void;
}

const RuleItem = ({ ruleKey, type, value, saved, disabled, onChange, onRemove }: RuleItemProps) => {
  const [events, setEvents] = useState({ alarm: value?.e || "" });
  const [delta, setDelta] = useState(`${value.delta}`);
  const [delay, setDelay] = useState(`${value.delay}`);
  const [hyst, setHyst] = useState(`${value.hyst}`);

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();
  const ruleTypes = useRuleTypes();

  const ruleTypeObject = useMemo(() => ruleTypes[ruleKey], [ruleKey, ruleTypes]);

  useEffect(() => {
    setEvents({ alarm: value?.e || "" });
    setDelta(`${value.delta}`);
    setDelay(`${value.delay}`);
    setHyst(`${value.hyst}`);
  }, [value]);

  return (
    <Grid item xs={12}>
      <TitledCard
        title={type.label}
        headerIcon={type.icon}
        outlined
        color={disabled ? lighten(theme.palette.secondary.main, 0.5) : theme.palette.secondary.main}
        postfix={
          !disabled && (
            <IconButton onClick={() => onRemove(ruleKey)} sx={{ mx: 1, p: 0 }}>
              <DeleteForeverOutlinedIcon />
            </IconButton>
          )
        }
      >
        <Grid container spacing={1} padding={1}>
          <ZoneEvent
            label={formatMessage({ defaultMessage: "Event" })}
            value={events}
            saved={saved ? { alarm: saved.e || "" } : undefined}
            onChange={({ alarm }) => {
              setEvents({ alarm });
              if (alarm) {
                value.e = alarm;
              } else {
                delete value.e;
              }
              onChange(ruleKey, value);
            }}
            disabled={disabled}
            color="secondary"
            noRecovery
            xs="auto"
            width={220}
          />
          <Grid item>
            <TimeTextField
              label={formatMessage({ defaultMessage: "Delay" })}
              unit={1000}
              variant="filled"
              value={delay}
              className={saved?.delay !== parseInt(delay, 10) ? editedStyle.input : ""}
              inputProps={{ min: 0, type: "number", onClick: onNumberInputClick }}
              onChange={(e) => setDelay(e.target.value)}
              onBlur={() => {
                value.delay = delay ? parseInt(delay, 10) : 0;
                onChange(ruleKey, value);
              }}
              color="secondary"
              sx={{ margin: 0, minWidth: 220, width: 220 }}
              size="small"
              autoComplete="off"
              disabled={disabled}
              fullWidth
            />
          </Grid>
          <Grid item>
            <TextField
              label={formatMessage({ defaultMessage: "Hysteresis ({measUnit})" }, { measUnit: ruleTypeObject?.measUnit })}
              variant="filled"
              value={hyst}
              className={saved?.hyst !== parseFloat(hyst) ? editedStyle.input : ""}
              inputProps={{ min: 0, step: 0.1, type: "number", onClick: onNumberInputClick }}
              onChange={(e) => setHyst(e.target.value)}
              onBlur={() => {
                value.hyst = hyst ? parseFloat(hyst) : 0;
                onChange(ruleKey, value);
              }}
              color="secondary"
              sx={{ margin: 0, minWidth: 160, width: 160 }}
              size="small"
              autoComplete="off"
              disabled={disabled}
              fullWidth
            />
          </Grid>
          <Grid item>
            <TextField
              label={formatMessage({ defaultMessage: "Delta ({measUnit})" }, { measUnit: ruleTypeObject?.measUnit })}
              variant="filled"
              value={delta}
              className={saved?.delta !== parseFloat(delta) ? editedStyle.input : ""}
              inputProps={{ min: 0, step: 0.1, type: "number", onClick: onNumberInputClick }}
              onChange={(e) => setDelta(e.target.value)}
              onBlur={() => {
                value.delta = delta ? parseFloat(delta) : 0;
                onChange(ruleKey, value);
              }}
              color="secondary"
              sx={{ margin: 0, minWidth: 160, width: 160 }}
              size="small"
              autoComplete="off"
              disabled={disabled}
              fullWidth
            />
          </Grid>
        </Grid>
      </TitledCard>
    </Grid>
  );
};

export default ReportItem;
